import { CheckCircle, Edit, MoreHoriz } from '@mui/icons-material';
import { Chip, useMediaQuery, useTheme } from '@mui/material';
import type { GridColDef, GridRenderCellParams, GridRowParams } from '@mui/x-data-grid-pro';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ActionButtonLink } from '~/components/button';
import { Link } from '~/components/link';
import { AuthorizedLink } from '~/components/links';
import { formatDate } from '~/lib/datetime';
import type { NetworkDeviceGroups__DeviceGroup as DeviceGroup } from '../../queries/list.generated';
import { Menu } from '../components';

export const useColumns = (): GridColDef<DeviceGroup>[] => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSmallAndUp = useMediaQuery(theme.breakpoints.up('sm'));

  return useMemo(
    () => [
      {
        field: 'name',
        flex: 1,
        headerName: t('common.labels.name'),
        minWidth: isSmallAndUp ? 180 : 0,
      },
      {
        field: 'defaultShow',
        headerName: t('pages.device_groups.list.columns.default_show'),
        sortable: false,
        renderCell: ({ row }: GridRenderCellParams<DeviceGroup>) => (
          <AuthorizedLink
            authorized={row.defaultShow?.canUpdate.value && !row.defaultShow.originId}
            entity={row.defaultShow}
          />
        ),

        minWidth: 250,
      },
      {
        field: 'enableReporting',
        headerName: t('pages.device_groups.list.columns.reporting'),
        headerAlign: 'center',
        align: 'center',
        sortable: false,
        renderCell: ({ row }: GridRenderCellParams<DeviceGroup>) => {
          return row.enableReporting ? <CheckCircle color="success" /> : null;
        },
      },
      {
        field: 'createdAt',
        headerName: t('common.labels.created'),
        renderCell: ({ row }: GridRenderCellParams<DeviceGroup>) => {
          return formatDate(row.createdAt);
        },
        minWidth: 250,
      },
      {
        field: 'devices',
        headerName: t('common.labels.devices'),
        headerAlign: 'center',
        align: 'center',
        sortable: false,
        renderCell: ({ row }: GridRenderCellParams<DeviceGroup>) => {
          return (
            <Chip
              color="primary"
              component={Link}
              label={row.deviceCount || 0}
              onClick={(event) => event.stopPropagation()}
              to={`${row.id}/devices`}
            />
          );
        },
        minWidth: 100,
      },
      {
        field: 'actions',
        type: 'actions',
        minWidth: isSmallAndUp ? 150 : 0,
        getActions: ({ row }: GridRowParams<DeviceGroup>) => {
          return [
            <ActionButtonLink
              key={`edit-${row.id}`}
              title={t('common.actions.edit')}
              Icon={Edit}
              to={`${row.id}`}
            />,
            <Menu
              key={`more-action-${row.id}`}
              deviceGroup={row}
              Icon={MoreHoriz}
              buttonType="action"
            />,
          ];
        },
      },
    ],
    [isSmallAndUp, t],
  );
};
